import {
  InvestmentType,
  PortfolioResolution,
  usePortfolioHistoryByAccountQuery,
} from '@transactions/graphql';
import {parseDate, today} from 'common/date';
import {ChevronDown, ChevronRight} from 'lucide-react';
import * as React from 'react';
import {Link} from 'react-router-dom';
import type {
  AllInvestmentAccountsPerformanceQuery,
  AllInvestmentAccountsQuery,
} from '@transactions/graphql';

type FleetAccount = AllInvestmentAccountsQuery['allInvestmentAccounts'][number];
type AccountPerformance =
  AllInvestmentAccountsPerformanceQuery['allInvestmentAccounts'][number]['performance'];

// Same freshness semantics as the accounts list (AccountRowV2): recent
// observations render nothing — quiet means healthy — a missed cadence gets
// a muted "as of" note, a twice-missed monthly routine turns amber.
const OBSERVATION_NOTE_DAYS = 14;
const OBSERVATION_STALE_DAYS = 60;

const GROUPS: Array<{key: string; label: string; types: InvestmentType[]}> = [
  {key: 'brokerage', label: 'Brokerage', types: [InvestmentType.Brokerage]},
  {
    key: 'retirement',
    label: 'Retirement',
    types: [
      InvestmentType['401K'],
      InvestmentType.Ira,
      InvestmentType.RothIra,
      InvestmentType.Hsa,
    ],
  },
  {key: 'education', label: 'Education', types: [InvestmentType.Education]},
  {key: 'other', label: 'Other', types: [InvestmentType.Other]},
];

const formatCurrency = (cents: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);

const formatShare = (share: number): string =>
  share >= 0.1 ? `${(share * 100).toFixed(1)}%` : `${(share * 100).toFixed(2)}%`;

const formatPercent = (decimal: number): string => {
  const sign = decimal >= 0 ? '+' : '';
  return `${sign}${(decimal * 100).toFixed(2)}%`;
};

const getSubtitle = (account: FleetAccount): string =>
  account.plaid?.institutionName ?? 'Manual investment';

type SparkPoint = {date: string; portfolioValue: number};

const Sparkline = ({points}: {points: SparkPoint[]}) => {
  if (points.length < 2) return null;
  const values = points.map((p) => p.portfolioValue);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(max - min, 1);
  // X is proportional to elapsed time, not array index — a sparse series
  // (manual accounts with observation gaps) must not compress months into
  // the same width as a week.
  const times = points.map((p) => new Date(p.date).getTime());
  const firstTime = times[0];
  const timeSpan = Math.max(times[times.length - 1] - firstTime, 1);
  const coords = values
    .map((v, i) => {
      const x = ((times[i] - firstTime) / timeSpan) * 106 + 2;
      const y = 24 - ((v - min) / span) * 20;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg
      width="110"
      height="28"
      viewBox="0 0 110 28"
      className="shrink-0"
      aria-hidden="true"
    >
      <polyline
        points={coords}
        fill="none"
        className="stroke-muted-foreground/60"
        strokeWidth="1.5"
      />
    </svg>
  );
};

type FleetCardProps = {
  account: FleetAccount;
  performance: AccountPerformance | undefined;
  /** Fraction of the positive-balance total; null for non-positive balances. */
  share: number | null;
  sparkline: SparkPoint[];
  /** When its checks don't balance, from when (else nothing). */
  balanceStatus: string | undefined;
};

const FleetCard = ({account, performance, share, sparkline, balanceStatus}: FleetCardProps) => {
  const xirr = performance?.xirr;
  // Freshness pairs with the observation backing the DISPLAYED value —
  // balanceAsOf follows the unbounded newest entry and can be future-dated.
  const observationAgeDays =
    account.presentBalanceAsOf != null
      ? today().diff(parseDate(account.presentBalanceAsOf), 'days').days
      : null;
  const showFreshness =
    observationAgeDays != null && observationAgeDays > OBSERVATION_NOTE_DAYS;

  return (
    <Link
      to={`/accounts/${account._id}`}
      className="border-border bg-card hover:bg-accent flex flex-col rounded-lg border p-3 no-underline transition-colors"
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-foreground truncate text-sm font-medium">
          {account.name}
        </span>
        {xirr != null && (
          <span
            className={`shrink-0 text-xs ${
              xirr > 0
                ? 'text-positive'
                : xirr < 0
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-muted-foreground'
            }`}
          >
            {formatPercent(xirr)} XIRR
          </span>
        )}
      </div>
      <span className="text-muted-foreground text-xs">
        {getSubtitle(account)}
      </span>
      <div className="mt-2 flex items-end justify-between gap-2">
        <span className="text-foreground text-lg font-medium">
          {formatCurrency(account.presentBalance)}
        </span>
        <Sparkline points={sparkline} />
      </div>
      {/* Share only exists for positive balances — a negative (margin
          debit) account renders its card without pretending to own a slice
          of the portfolio. */}
      {share != null && (
        <div className="bg-muted mt-2 h-1 rounded-full">
          <div
            className="bg-muted-foreground/60 h-1 rounded-full"
            style={{width: `${Math.min(Math.max(share * 100, 1), 100)}%`}}
          />
        </div>
      )}
      <div className="mt-1 flex items-center justify-between">
        <span className="text-muted-foreground text-[11px]">
          {share != null ? `${formatShare(share)} of portfolio` : ''}
        </span>
        {showFreshness && (
          <span
            className={
              (observationAgeDays ?? 0) > OBSERVATION_STALE_DAYS
                ? 'text-[11px] text-amber-600 dark:text-amber-500'
                : 'text-muted-foreground text-[11px]'
            }
          >
            as of {account.presentBalanceAsOf}
          </span>
        )}
      </div>
      {/* Like freshness, quiet when all's well: shown only when the checks
          don't balance, from the last day they did. */}
      {balanceStatus != null && <span className="text-warning mt-0.5 text-right text-[11px]">{balanceStatus}</span>}
    </Link>
  );
};

type InvestmentFleetProps = {
  /** Open (non-closed) investment accounts, funded and dormant alike. */
  accounts: FleetAccount[];
  performanceByAccountId: Map<string, AccountPerformance>;
  /** For an account whose checks don't balance, from when (else nothing). */
  balanceStatusOf: (accountId: string) => string | undefined;
};

/**
 * The fleet view: the Overview tab's browse layout. Funded accounts render
 * as sparkline cards grouped with subtotals; zero-balance dormant accounts
 * fold into one disclosure so history doesn't read as clutter. Search and
 * the status/type filters flatten back to the plain row list — the fleet is
 * the unfiltered view.
 */
export const InvestmentFleet = ({
  accounts,
  performanceByAccountId,
  balanceStatusOf,
}: InvestmentFleetProps) => {
  const [dormantOpen, setDormantOpen] = React.useState(false);

  // One batched request supplies every card's trailing-year sparkline. The
  // explicit endDate clamps at today — recordBalanceAnchor permits
  // future-dated anchors, and a scheduled balance must not render as
  // historical performance.
  const yearAgo = today().minus({years: 1}).toISODate() ?? undefined;
  const todayISO = today().toISODate() ?? undefined;
  const {data: historyData} = usePortfolioHistoryByAccountQuery({
    variables: {
      resolution: PortfolioResolution.Weekly,
      startDate: yearAgo,
      endDate: todayISO,
    },
    fetchPolicy: 'cache-and-network',
  });
  const sparklines = React.useMemo(
    () =>
      new Map(
        (historyData?.portfolioHistoryByAccount ?? []).map((h) => {
          // The shared-grid series zero-fills before an account's first
          // observation; those synthetic points would draw a young account
          // as a long verified-zero shelf plus a jump. The API marks them
          // unobserved — trim exactly those, so a genuinely observed idle
          // zero stretch stays visible.
          let firstObserved = 0;
          while (
            firstObserved < h.points.length &&
            !h.points[firstObserved].observed
          ) {
            firstObserved++;
          }
          return [h.accountId, h.points.slice(firstObserved)];
        }),
      ),
    [historyData],
  );

  // Classification, shares, and displayed amounts all read presentBalance
  // — the today-clamped value — because `balance` follows the newest entry
  // even when it is future-dated, so a scheduled zero would prematurely
  // fold a funded account into Dormant and a scheduled increase would
  // distort today's shares. Cards render for any nonzero present balance
  // (negative margin-debit accounts are real and must stay visible), but
  // the share denominator sums positive balances only.
  const funded = accounts.filter((account) => account.presentBalance !== 0);
  const dormant = accounts.filter((account) => account.presentBalance === 0);
  const dormantUnbalanced = dormant.filter((account) => balanceStatusOf(account._id) != null).length;
  const totalValue = funded.reduce(
    (sum, account) => sum + Math.max(account.presentBalance, 0),
    0,
  );

  return (
    <div className="flex flex-col gap-4">
      {GROUPS.map((group) => {
        const members = funded
          .filter((account) => group.types.includes(account.investmentType))
          .sort((a, b) => b.presentBalance - a.presentBalance);
        if (members.length === 0) return null;
        // The header's dollar figure is the honest net; its share matches
        // the cards by using positive balances only in both operands.
        const subtotal = members.reduce(
          (sum, account) => sum + account.presentBalance,
          0,
        );
        const positiveSubtotal = members.reduce(
          (sum, account) => sum + Math.max(account.presentBalance, 0),
          0,
        );
        return (
          <div key={group.key}>
            <div className="mb-2 flex items-baseline gap-2">
              <span className="text-foreground text-sm font-medium">
                {group.label}
              </span>
              <span className="text-muted-foreground text-xs">
                {formatCurrency(subtotal)}
                {totalValue > 0 &&
                  positiveSubtotal > 0 &&
                  ` · ${formatShare(positiveSubtotal / totalValue)}`}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-3">
              {members.map((account) => (
                <FleetCard
                  key={account._id}
                  account={account}
                  performance={performanceByAccountId.get(account._id)}
                  share={
                    account.presentBalance > 0 && totalValue > 0
                      ? account.presentBalance / totalValue
                      : null
                  }
                  sparkline={sparklines.get(account._id) ?? []}
                  balanceStatus={balanceStatusOf(account._id)}
                />
              ))}
            </div>
          </div>
        );
      })}
      {dormant.length > 0 && (
        <div className="border-border rounded-lg border border-dashed">
          <button
            type="button"
            onClick={() => setDormantOpen((open) => !open)}
            className="text-muted-foreground flex w-full cursor-pointer items-center gap-2 p-3 text-sm"
          >
            {dormantOpen ? (
              <ChevronDown className="size-4 shrink-0" />
            ) : (
              <ChevronRight className="size-4 shrink-0" />
            )}
            <span>
              {dormant.length} dormant account{dormant.length !== 1 && 's'} —
              zero balance
            </span>
            {/* Folded, a dormant account that doesn't balance still shows. */}
            {dormantUnbalanced > 0 && (
              <span className="text-warning shrink-0 text-xs">{dormantUnbalanced} not balanced</span>
            )}
            {!dormantOpen && (
              <span className="text-muted-foreground/60 ml-auto hidden truncate text-xs sm:block">
                {dormant
                  .slice(0, 4)
                  .map((account) => account.name)
                  .join(' · ')}
                {dormant.length > 4 && ` · +${dormant.length - 4} more`}
              </span>
            )}
          </button>
          {dormantOpen && (
            <div className="border-border grid grid-cols-1 gap-1 border-t p-2 md:grid-cols-2 xl:grid-cols-3">
              {dormant.map((account) => (
                <Link
                  key={account._id}
                  to={`/accounts/${account._id}`}
                  className="hover:bg-accent flex items-baseline justify-between gap-2 rounded-md px-2 py-1.5 no-underline"
                >
                  <span className="text-muted-foreground truncate text-sm">
                    {account.name}
                  </span>
                  {/* A zero balance can still be out of balance (cash or
                      shares the activity doesn't account for). */}
                  <span
                    className={`shrink-0 text-xs ${balanceStatusOf(account._id) != null ? 'text-warning' : 'text-muted-foreground/60'}`}
                  >
                    {balanceStatusOf(account._id) ?? getSubtitle(account)}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
